import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, X, AlertTriangle, Loader2, Zap, RotateCcw, Search, ListOrdered, Dice5 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Badge } from 'components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import * as TestModule from 'modules/test';
import { useSubjectList } from 'modules/subject';
import type { Subject } from 'modules/subject/types';
import type { TestSubjectConfig } from 'modules/questionBank/types';
import { http } from 'services';
import { useQueryClient } from '@tanstack/react-query';

// ─── SubjectConfigCard ────────────────────────────────────────────────────────

interface SubjectConfigCardProps {
  config: TestSubjectConfig;
  availableCount: number;
  disabled?: boolean;
  isActionPending?: boolean;
  onUpdate: (partial: Partial<TestSubjectConfig>) => void;
  onRemove: () => void;
  onRequestRefresh: (subjectId: string, mode: 'ORDERED' | 'RANDOM') => void;
}

const SubjectConfigCard: React.FC<SubjectConfigCardProps> = ({
  config,
  availableCount,
  disabled,
  isActionPending,
  onUpdate,
  onRemove,
  onRequestRefresh,
}) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: config.subjectId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isInsufficient = availableCount < config.questionCount;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'border rounded-lg p-4 bg-card flex gap-3 transition-shadow',
        isDragging && 'shadow-lg opacity-80',
      )}
    >
      {/* Drag handle */}
      {!disabled ? (
        <button
          type="button"
          aria-label={t('drag_to_reorder', 'Drag to reorder')}
          className="text-muted-foreground/40 hover:text-muted-foreground cursor-grab active:cursor-grabbing flex-shrink-0 self-start mt-0.5"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-5 h-5" />
        </button>
      ) : (
        <div className="w-5 flex-shrink-0" />
      )}

      {/* Content */}
      <div className="flex-1">
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <h3 className="font-medium break-words leading-snug">{config.subjectName}</h3>
            {config.mode === 'RANDOM' ? (
              <Badge variant="warning" size="sm" className="gap-1 font-normal text-[11px] py-0">
                <Dice5 className="w-3 h-3" />
                {t('mode_randomized', 'Randomized')}
              </Badge>
            ) : (
              <Badge variant="primary" size="sm" className="gap-1 font-normal text-[11px] py-0">
                <ListOrdered className="w-3 h-3" />
                {t('mode_in_order', 'In Order')}
              </Badge>
            )}
          </div>
          {!disabled && (
            <button
              type="button"
              className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
              onClick={onRemove}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* One grid rather than two independent flex rows: wrapping used to
            leave each field's input wherever its own label ended, so the two
            number boxes never lined up once they stacked. Shared columns keep
            them aligned at every width. */}
        <div className="grid grid-cols-[1fr_5rem_auto] items-center gap-x-2 gap-y-2.5 sm:grid-cols-[auto_5rem_auto] sm:justify-start sm:gap-x-3">
          <label className="text-sm text-muted-foreground">{t('draw')}</label>
          <Input
            type="number"
            min={1}
            max={availableCount || 100}
            value={config.questionCount}
            disabled={disabled}
            onChange={(e) => onUpdate({ questionCount: Math.max(1, Number(e.target.value)) })}
            className="w-full h-8 text-sm"
          />
          <span className="text-sm text-muted-foreground">{t('questions_lower')}</span>

          <label className="text-sm text-muted-foreground">{t('points_each')}</label>
          <Input
            type="number"
            min={0}
            step={0.5}
            value={config.pointsPerQuestion}
            disabled={disabled}
            onChange={(e) => onUpdate({ pointsPerQuestion: Math.max(0, Number(e.target.value)) })}
            className="w-full h-8 text-sm"
          />
        </div>

        <div className="mt-3 pt-3 border-t flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {isInsufficient ? (
              <>
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                <span className="text-amber-600 font-medium">
                  {t('only_n_questions_available_drawing_fewer', { count: availableCount })}
                </span>
              </>
            ) : (
              <span>{t('n_questions_available_in_bank', { count: availableCount })}</span>
            )}
          </div>

          {!disabled && (
            <div className="flex items-center gap-2 ml-auto">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs px-2.5"
                onClick={() => onRequestRefresh(config.subjectId, 'ORDERED')}
                disabled={disabled || isActionPending || isInsufficient}
              >
                <ListOrdered className="mr-1 h-3.5 w-3.5 text-muted-foreground" />
                {t('set_in_order', 'In Order')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs px-2.5 text-amber-700 dark:text-amber-400 hover:text-amber-800 border-amber-200 dark:border-amber-800"
                onClick={() => onRequestRefresh(config.subjectId, 'RANDOM')}
                disabled={disabled || isActionPending || isInsufficient}
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" />
                {t('regenerate', 'Regenerate')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── SubjectBuilderPage ───────────────────────────────────────────────────────

export const SubjectBuilderPage: React.FC = () => {
  const { t } = useTranslation();
  const { testId } = useParams<{ testId: string }>();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { item: test } = TestModule.Hooks.useSingle({ id: testId ?? '' });
  const activate = TestModule.Hooks.useActivate();
  const { data: allSubjects = [] } = useSubjectList();

  const [configs, setConfigs] = useState<TestSubjectConfig[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [subjectSearch, setSubjectSearch] = useState('');
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [confirmSubjectAction, setConfirmSubjectAction] = useState<{
    subjectId: string;
    subjectName: string;
    mode: 'ORDERED' | 'RANDOM';
  } | null>(null);
  const [isRefreshingSubject, setIsRefreshingSubject] = useState(false);

  const isLocked = ((test as any)?.stats?.activeAttempts ?? 0) > 0;

  // Hydrate from test when loaded
  useEffect(() => {
    if (test?.subjectConfigs) {
      setConfigs([...test.subjectConfigs].sort((a, b) => a.order - b.order));
    }
  }, [test?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setConfigs((prev) => {
      const oldIdx = prev.findIndex((c) => c.subjectId === active.id);
      const newIdx = prev.findIndex((c) => c.subjectId === over.id);
      return arrayMove(prev, oldIdx, newIdx).map((c, i) => ({ ...c, order: i }));
    });
  };

  const addSubject = (subject: Subject) => {
    if (configs.some((c) => c.subjectId === subject.id)) return;
    setConfigs((prev) => [
      ...prev,
      {
        subjectId: subject.id,
        subjectName: subject.name,
        questionCount: Math.min(5, subject.questionCount || 5),
        pointsPerQuestion: 1,
        order: prev.length,
        mode: 'ORDERED',
      },
    ]);
    setSubjectSearch('');
    setAddDialogOpen(false);
  };

  const removeConfig = (subjectId: string) => {
    setConfigs((prev) =>
      prev.filter((c) => c.subjectId !== subjectId).map((c, i) => ({ ...c, order: i })),
    );
  };

  const updateConfig = (subjectId: string, partial: Partial<TestSubjectConfig>) => {
    setConfigs((prev) =>
      prev.map((c) => (c.subjectId === subjectId ? { ...c, ...partial } : c)),
    );
  };

  const saveConfiguration = async () => {
    if (!testId || isLocked) return;
    setIsSaving(true);
    try {
      await http.request.patch(`/api/tests/${testId}/config/subject-builder`, { configs });
      await queryClient.invalidateQueries({ queryKey: ['test', 'single', testId] });
      toast.success(t('configuration_and_questions_saved', 'Configuration and questions saved successfully'));
    } catch (err: any) {
      const msg = err?.response?.data?.message || t('failed_to_save_configuration');
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubjectRefresh = async () => {
    if (!testId || isLocked || !confirmSubjectAction) return;
    setIsRefreshingSubject(true);
    try {
      await http.request.post(
        `/api/tests/${testId}/config/subjects/${confirmSubjectAction.subjectId}/refresh`,
        {
          mode: confirmSubjectAction.mode,
          configs,
        },
      );
      await queryClient.invalidateQueries({ queryKey: ['test', 'single', testId] });
      toast.success(
        confirmSubjectAction.mode === 'ORDERED'
          ? t('subject_questions_ordered_success', 'Subject questions set in chronological order')
          : t('subject_questions_regenerated_success', 'Subject questions regenerated randomly'),
      );
      setConfirmSubjectAction(null);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        t('failed_to_refresh_subject_questions', 'Failed to refresh subject questions');
      toast.error(msg);
    } finally {
      setIsRefreshingSubject(false);
    }
  };

  const handleActivate = async () => {
    if (!testId) return;
    await saveConfiguration();
    await activate.mutateAsync({ id: testId });
    setActivateDialogOpen(false);
  };

  const totalQuestions = configs.reduce((s, c) => s + c.questionCount, 0);
  const totalPoints = configs.reduce((s, c) => s + c.questionCount * c.pointsPerQuestion, 0);

  const availableSubjectsToAdd = (allSubjects as Subject[]).filter(
    (s) => !configs.some((c) => c.subjectId === s.id),
  );

  const filteredAvailableSubjects = availableSubjectsToAdd.filter((s) => {
    const q = subjectSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      s.name.toLowerCase().includes(q) ||
      (s.description ? s.description.toLowerCase().includes(q) : false)
    );
  });

  const getAvailableCount = (subjectId: string) =>
    (allSubjects as Subject[]).find((s) => s.id === subjectId)?.questionCount ?? 0;

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      {isLocked && (
        <div className="mb-6 rounded-lg border border-amber-300/80 bg-amber-50 dark:bg-amber-950/30 p-4 flex gap-3 text-amber-900 dark:text-amber-200">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold">
              {t('test_active_sessions_locked_title', 'Active test sessions in progress')}
            </p>
            <p className="mt-0.5 text-amber-800 dark:text-amber-300">
              {t('test_active_sessions_locked_desc', 'Candidates currently have active sessions taking this test. To protect their session, questions cannot be modified until those sessions finish.')}
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold">{t('subject_builder')}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t('subject_builder_description')}
          </p>
        </div>
        <Button
          variant="outline"
          className="w-full sm:w-auto"
          onClick={() => {
            setSubjectSearch('');
            setAddDialogOpen(true);
          }}
          disabled={isLocked}
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('add_subject')}
        </Button>
      </div>

      {configs.length === 0 ? (
        <div className="text-center py-16 border rounded-lg text-muted-foreground">
          <p className="text-sm">{t('no_subjects_added_yet')}</p>
          <Button
            variant="outline"
            className="mt-3"
            onClick={() => {
              setSubjectSearch('');
              setAddDialogOpen(true);
            }}
            disabled={isLocked}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('add_subject')}
          </Button>
        </div>
      ) : (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={configs.map((c) => c.subjectId)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3">
                {configs.map((config) => (
                  <SubjectConfigCard
                    key={config.subjectId}
                    config={config}
                    availableCount={getAvailableCount(config.subjectId)}
                    disabled={isLocked}
                    isActionPending={isRefreshingSubject}
                    onUpdate={(partial) => updateConfig(config.subjectId, partial)}
                    onRemove={() => removeConfig(config.subjectId)}
                    onRequestRefresh={(subjectId, mode) => {
                      const subj = configs.find((c) => c.subjectId === subjectId);
                      if (subj) {
                        setConfirmSubjectAction({
                          subjectId,
                          subjectName: subj.subjectName,
                          mode,
                        });
                      }
                    }}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <div className="mt-4 text-sm text-muted-foreground border-t pt-4 flex justify-between items-center">
            <span>
              {t('subject_builder_totals', { questions: totalQuestions, points: totalPoints })}
            </span>
          </div>
        </>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-3 mt-6 sm:flex-row sm:flex-wrap">
        <Button
          variant="outline"
          className="w-full sm:w-auto"
          onClick={saveConfiguration}
          disabled={isSaving || configs.length === 0 || isLocked}
        >
          {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {t('save_configuration')}
        </Button>
        <Button
          className="w-full sm:w-auto"
          onClick={() => setActivateDialogOpen(true)}
          disabled={configs.length === 0 || test?.status === 'active'}
        >
          <Zap className="w-4 h-4 mr-2" />
          {test?.status === 'active' ? t('already_active') : t('activate_assessment')}
        </Button>
      </div>

      {/* Add Subject Dialog */}
      <Dialog
        open={addDialogOpen}
        onOpenChange={(open) => {
          setAddDialogOpen(open);
          if (!open) setSubjectSearch('');
        }}
      >
        <DialogContent className="max-w-md w-full max-h-[85vh] sm:max-h-[80vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-5 pb-3 border-b shrink-0">
            <DialogTitle>{t('add_subject')}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-1">
              {t('select_subject')}
            </DialogDescription>
          </DialogHeader>

          {availableSubjectsToAdd.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {t('all_subjects_added_or_none_exist')}
            </div>
          ) : (
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
              {/* Search bar */}
              <div className="p-4 pb-2 shrink-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                  <Input
                    type="text"
                    value={subjectSearch}
                    onChange={(e) => setSubjectSearch(e.target.value)}
                    placeholder={t('search_subjects', 'Search subjects...')}
                    className="pl-9 pr-8 h-9 text-sm"
                    autoFocus
                  />
                  {subjectSearch && (
                    <button
                      type="button"
                      onClick={() => setSubjectSearch('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-0.5 rounded-sm"
                      aria-label={t('clear_filters', 'Clear search')}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Scrollable list of subjects */}
              <div
                className="flex-1 overflow-y-auto px-4 pb-4 space-y-2 min-h-0 max-h-[400px] overscroll-contain"
                tabIndex={0}
                role="region"
                aria-label={t('subjects', 'Subjects')}
              >
                {filteredAvailableSubjects.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground space-y-2">
                    <p>{t('no_subjects_match', 'No subjects match')}</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setSubjectSearch('')}
                      className="text-xs h-7"
                    >
                      {t('clear_filters', 'Clear filters')}
                    </Button>
                  </div>
                ) : (
                  filteredAvailableSubjects.map((subject) => (
                    <button
                      key={subject.id}
                      type="button"
                      onClick={() => addSubject(subject)}
                      className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 hover:border-foreground/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring text-left transition-colors group cursor-pointer"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className="w-8 h-8 rounded-md flex-shrink-0"
                          style={{ backgroundColor: subject.color ?? '#6366f1' }}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm break-words leading-snug [.group:hover_&]:underline">
                            {subject.name}
                          </p>
                          <p className="text-xs text-muted-foreground break-words mt-0.5">
                            {t('n_questions_in_bank', { count: subject.questionCount })}
                          </p>
                        </div>
                      </div>
                      <Plus className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-colors [.group:hover_&]:text-foreground" />
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Activate Confirmation */}
      <AlertDialog open={activateDialogOpen} onOpenChange={setActivateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('activate_assessment_question')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('activate_assessment_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleActivate} disabled={activate.isPending}>
              {activate.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('activate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Per-Subject Refresh / Regenerate Confirmation */}
      <AlertDialog open={!!confirmSubjectAction} onOpenChange={(open) => !open && setConfirmSubjectAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmSubjectAction?.mode === 'ORDERED'
                ? t('set_subject_in_order_title', 'Set questions in chronological order?')
                : t('regenerate_subject_title', 'Regenerate questions randomly?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmSubjectAction?.mode === 'ORDERED'
                ? t(
                    'set_subject_in_order_desc',
                    'This will replace questions for "{{subject}}" with the first {{count}} questions from the subject question bank in chronological order.',
                    {
                      subject: confirmSubjectAction?.subjectName,
                      count: configs.find((c) => c.subjectId === confirmSubjectAction?.subjectId)?.questionCount,
                    },
                  )
                : t(
                    'regenerate_subject_desc',
                    'This will replace questions for "{{subject}}" with a random selection of {{count}} questions from the subject question bank.',
                    {
                      subject: confirmSubjectAction?.subjectName,
                      count: configs.find((c) => c.subjectId === confirmSubjectAction?.subjectId)?.questionCount,
                    },
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRefreshingSubject}>
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isRefreshingSubject}
              onClick={(e) => {
                e.preventDefault();
                handleSubjectRefresh();
              }}
              className={
                confirmSubjectAction?.mode === 'RANDOM'
                  ? 'bg-amber-600 hover:bg-amber-700 text-white'
                  : undefined
              }
            >
              {isRefreshingSubject && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {confirmSubjectAction?.mode === 'ORDERED'
                ? t('set_in_order', 'In Order')
                : t('regenerate', 'Regenerate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
